import { useEffect, useRef, useState } from "react";
import { Bell, Heart, MessageCircle, CheckCircle2, XCircle, Trash2, CheckCheck, UserPlus, AtSign, Send } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { useAuth, authClient } from "@/lib/auth";
import creatorCommunityApi from "@/lib/creatorCommunityApi";
import { useRealtime } from "@/lib/realtime";
import { sfx } from "@/lib/sounds";
import { toast } from "sonner";

const TYPE_ICON = { like: Heart, comment: MessageCircle, approved: CheckCircle2, rejected: XCircle, follow: UserPlus, follow_request: UserPlus, build_submitted: Bell, mention: AtSign, message: Send, community_message: MessageCircle, community_reply: MessageCircle };
const TYPE_COLOR = { like: "#FF3B7A", comment: "#3ABEFF", approved: "#22c55e", rejected: "#FF3B3B", follow: "#FFBF00", follow_request: "#FFBF00", build_submitted: "#FFBF00", mention: "#FF8C00", message: "#3ABEFF", community_message: "#3ABEFF", community_reply: "#FFBF00" };
const TYPE_LABEL = { like: "liked", comment: "commented on", approved: "approved", rejected: "rejected", follow: "follow", follow_request: "requested to follow", build_submitted: "submitted a build", mention: "mentioned", message: "message", community_message: "community message", community_reply: "community reply" };

function normalizeNotification(raw = {}) {
  return {
    ...raw,
    type: raw.type || raw.kind || "system",
    actor_name: raw.actor_name || raw.actor || "A user",
    body: raw.body || raw.message || "",
    subject_title: raw.subject_title || raw.title || raw.meta?.community_name || "",
    community_name: raw.community_name || raw.meta?.community_name || "",
  };
}

export default function NotificationsBell() {
  const { user } = useAuth();
  const rt = useRealtime();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const seenNotifIdsRef = useRef(new Set());
  const communityNameCacheRef = useRef(new Map());

  const enrichCommunityNotification = async (raw) => {
    const n = normalizeNotification(raw);

    if (!["community_message", "community_reply"].includes(n.type)) return n;

    const communityId = n.meta?.community_id;
    if (!communityId) return n;

    const cachedName = communityNameCacheRef.current.get(communityId);
    if (cachedName) return { ...n, community_name: cachedName };

    try {
      const { data } = await creatorCommunityApi.get(
        `/communities/${encodeURIComponent(communityId)}`
      );

      const community = data?.community || data;
      const name =
        community?.name ||
        community?.title ||
        community?.display_name ||
        community?.community_name ||
        "";

      if (name) {
        communityNameCacheRef.current.set(communityId, name);
        return { ...n, community_name: name };
      }
    } catch (err) {
      console.warn("Could not resolve community notification name:", err?.message);
    }

    return n;
  };

  const loadCount = async () => {
    try {
      const { data } = await authClient.get("/notifications/unread-count");
      setUnread(data.count || 0);
    } catch (err) { console.warn("notif count failed", err?.message); }
  };

  const loadList = async () => {
    try {
      const { data } = await authClient.get("/notifications");
      const normalized = await Promise.all(
        (Array.isArray(data) ? data : []).map(enrichCommunityNotification)
      );
      setItems(normalized);
      normalized.forEach((n) => { if (n.id) seenNotifIdsRef.current.add(n.id); });
    } catch (err) { console.warn("notif list failed", err?.message); }
  };

  useEffect(() => {
    if (!user) { setItems([]); setUnread(0); seenNotifIdsRef.current.clear(); return; }
    loadCount();
    loadList();
    // Fallback poll (only fires when WS is disconnected)
    const id = setInterval(() => { if (!rt.connected) loadCount(); }, 25000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Realtime — push notifications instantly to the list + badge
  useEffect(() => {
    if (!user) return () => {};
    const un = rt.subscribe("notification", async (msg) => {
      const n = await enrichCommunityNotification(msg.notification);
      if (!n || !n.id || seenNotifIdsRef.current.has(n.id)) return;

      seenNotifIdsRef.current.add(n.id);
      setItems((prev) => [n, ...prev].slice(0, 40));

      if (!n.read) {
        setUnread((u) => u + 1);
      }
    });
    return () => un();
  }, [user, rt]);

  useEffect(() => {
    if (open && user) loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const markRead = async (id) => {
    setItems(items.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread(Math.max(0, unread - 1));
    try { await authClient.post(`/notifications/read/${id}`); }
    catch (err) { console.warn("mark read failed", err?.message); }
  };

  const markAllRead = async () => {
    setItems(items.map((n) => ({ ...n, read: true })));
    setUnread(0);
    sfx.click();
    try { await authClient.post("/notifications/read-all"); }
    catch (err) { console.warn("mark all read failed", err?.message); }
  };

  const remove = async (id) => {
    setItems(items.filter((n) => n.id !== id));
    seenNotifIdsRef.current.delete(id);
    try { await authClient.delete(`/notifications/${id}`); }
    catch (err) { console.warn("remove notif failed", err?.message); }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all notifications?")) return;
    setItems([]); setUnread(0); seenNotifIdsRef.current.clear();
    sfx.no();
    try { await authClient.delete("/notifications"); toast.success("Cleared"); }
    catch (err) { console.warn("clear notifs failed", err?.message); toast.error("Failed to clear"); }
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="nav-notifications"
          onClick={() => sfx.click()}
          className="relative p-2 rounded-full hover:bg-white/5 border border-transparent hover:border-white/10 text-white/80"
          aria-label="Notifications"
        >
          <Bell size={18} />
          {unread > 0 && (
            <span
              data-testid="notif-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FFBF00] text-black text-[10px] font-bold grid place-items-center leading-none"
              style={{ boxShadow: "0 0 12px rgba(255,191,0,0.6)" }}
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 max-w-[92vw] p-0 bg-black/95 border border-white/10 text-white backdrop-blur-xl" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFBF00]">Notifications</div>
          <div className="flex items-center gap-1">
            {unread > 0 && (
              <button
                data-testid="notif-mark-all-read"
                onClick={markAllRead}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/70 hover:text-[#FFBF00] px-2 py-1 rounded-md hover:bg-white/5"
              >
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
            {items.length > 0 && (
              <button
                data-testid="notif-clear-all"
                onClick={clearAll}
                className="text-[10px] uppercase tracking-widest text-white/40 hover:text-red-400 px-2 py-1 rounded-md hover:bg-white/5"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto divide-y divide-white/5">
          {items.length === 0 && (
            <div className="text-center text-white/40 text-sm px-4 py-10">
              You're all caught up.
            </div>
          )}
          {items.map((n) => {
            const Icon = TYPE_ICON[n.type] || Bell;
            const color = TYPE_COLOR[n.type] || "#FFBF00";
            const label = TYPE_LABEL[n.type] || n.type;
            let when = "";
            try { when = formatDistanceToNow(new Date(n.created_at), { addSuffix: true }); } catch (err) { console.debug("date fmt failed", err?.message); }
            return (
              <div
                key={n.id}
                data-testid={`notif-item-${n.id}`}
                className={`group flex items-start gap-3 px-4 py-3 hover:bg-white/5 ${n.read ? "opacity-70" : ""}`}
              >
                {n.actor_avatar ? (
                  <div className="relative">
                    <img src={n.actor_avatar} alt="" className="w-9 h-9 rounded-md object-cover" />
                    <div
                      className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full grid place-items-center border-2 border-black"
                      style={{ background: color }}
                    >
                      <Icon size={10} className="text-black" fill="currentColor" />
                    </div>
                  </div>
                ) : (
                  <div className="w-9 h-9 rounded-md grid place-items-center" style={{ background: `${color}33`, color }}>
                    <Icon size={14} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <Link
                    to={
                      n.link && n.link !== "/" ? n.link :
                      n.subject_type === "build" ? "/builds"
                      : n.subject_type === "post" ? `/community#${n.subject_id || ""}`
                      : n.subject_type === "user" ? `/users/${n.subject_id || ""}`
                      : n.subject_type === "follow_request" ? "/follow-requests"
                      : n.subject_type === "conversation" ? `/messages?c=${n.subject_id || ""}`
                      : "/"
                    }
                    onClick={() => { markRead(n.id); setOpen(false); }}
                    className="text-sm"
                  >
                    {n.type === "follow" ? (
                      <>
                        <span className="font-semibold text-white">{n.actor_name || "A user"}</span>{" "}
                        <span className="text-white/70">started following you</span>
                      </>
                    ) : n.type === "follow_request" ? (
                      <>
                        <span className="font-semibold text-white">{n.actor_name || "A user"}</span>{" "}
                        <span className="text-white/70">requested to follow you</span>
                      </>
                    ) : n.type === "community_message" ? (
                      <>
                        <span className="font-semibold text-white">{n.actor_name || "A user"}</span>{" "}
                        <span className="text-white/70">sent a message in</span>{" "}
                        <span className="font-semibold text-[#FFBF00]">{n.community_name || n.subject_title || "a community"}</span>
                      </>
                    ) : n.type === "community_reply" ? (
                      <>
                        <span className="font-semibold text-white">{n.actor_name || "A user"}</span>{" "}
                        <span className="text-white/70">replied in</span>{" "}
                        <span className="font-semibold text-[#FFBF00]">{n.community_name || n.subject_title || "a community"}</span>
                      </>
                    ) : n.type === "message" ? (
                      <>
                        <span className="font-semibold text-white">{n.actor_name || "A user"}</span>{" "}
                        <span className="text-white/70">sent you a message</span>
                      </>
                    ) : n.type === "build_submitted" ? (
                      <>
                        <span className="font-semibold text-white">{n.actor_name || "A user"}</span>{" "}
                        <span className="text-white/70">submitted a build:</span>{" "}
                        <span className="font-semibold text-[#FFBF00]">{n.subject_title || "New build"}</span>
                      </>
                    ) : (n.type === "approved" || n.type === "rejected") && n.subject_type === "build" ? (
                      <>
                        <span className="text-white/80">Your build</span>{" "}
                        <span className="font-semibold text-[#FFBF00]">{n.subject_title || "Build"}</span>{" "}
                        <span className="text-white/70">{n.type === "approved" ? "was approved" : "was rejected"}</span>
                      </>
                    ) : n.type === "system" && n.subject_type === "post" ? (
                      <>
                        <span className="font-semibold text-white">{n.actor_name || "A user"}</span>{" "}
                        <span className="text-white/70">published a community post</span>
                      </>
                    ) : n.type === "system" ? (
                      <span className="text-white/80">
                        {n.body || n.message || n.subject_title || "New notification"}
                      </span>
                    ) : n.type === "comment" ? (
                      <>
                        <span className="font-semibold text-white">{n.actor_name || "A user"}</span>{" "}
                        <span className="text-white/70">
                          {n.subject_type === "build" ? "commented on your build" : "commented on your post"}
                        </span>
                      </>
                    ) : n.type === "like" ? (
                      <>
                        <span className="font-semibold text-white">{n.actor_name || "A user"}</span>{" "}
                        <span className="text-white/70">
                          {n.subject_type === "build" ? "liked your build" : "liked your post"}
                        </span>
                      </>
                    ) : n.type === "mention" ? (
                      <>
                        <span className="font-semibold text-white">{n.actor_name || "A user"}</span>{" "}
                        <span className="text-white/70">mentioned you</span>
                      </>
                    ) : (
                      <>
                        <span className="font-semibold text-white">{n.actor_name || "A user"}</span>{" "}
                        <span className="text-white/60">{label}</span>{" "}
                        {n.subject_title && (
                          <span className="font-semibold text-[#FFBF00]">{n.subject_title}</span>
                        )}
                      </>
                    )}
                  </Link>
                  {n.body && ["comment", "message", "mention", "community_message", "community_reply"].includes(n.type) && (
                    <div className="text-xs text-white/60 mt-1 truncate">"{n.body}"</div>
                  )}
                  {n.body && n.type === "system" && n.subject_type === "post" && (
                    <div className="text-xs text-white/60 mt-1 truncate">"{n.body}"</div>
                  )}
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">{when}</div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!n.read && (
                    <button
                      data-testid={`notif-read-${n.id}`}
                      onClick={() => markRead(n.id)}
                      className="p-1 rounded hover:bg-white/10 text-white/50 hover:text-[#FFBF00]"
                      aria-label="Mark read"
                    >
                      <CheckCircle2 size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => remove(n.id)}
                    className="p-1 rounded hover:bg-red-500/10 text-white/40 hover:text-red-400"
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-[#FFBF00] shrink-0 mt-2" />}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
